import {SignOut, UserCircle} from '@phosphor-icons/react';
import {Box, ButtonBase, Divider, Typography} from '@mui/material';
import {
  Station,
  UserInfo,
  WeatherStationFromList,
  selectMyWeatherSections,
  selectUserInfo,
} from '@smartmixin/core';
import {messages} from '@smartmixin/i18n';
import React from 'react';
import isEqual from 'react-fast-compare';
import {useIntl} from 'react-intl';
import {useSelector} from 'react-redux';
import {useLocation} from 'react-router';
import Logo from '../../components/Logo';
import {IconButton} from '../../components/ui';
import RouterLink from '../../lib/RouterLink';
import {placeHref} from '../../screens/myWeather/placeHref';
import {NavGroup, NavLink} from './NavLink';
import {ThemeToggleButton} from './ThemeToggleButton';
import {useLogout} from './useLogout';
import {NavItem, useNavItems} from './useNavItems';

/** Favourites shown before "See all": the sidebar is navigation, not a list. */
export const SIDEBAR_FAVORITES_MAX = 5;

/** Equal once decoded: `pt:…` typed by hand is `pt%3A…` from a link. */
const samePath = (a: string, b: string) => {
  try {
    return decodeURIComponent(a) === decodeURIComponent(b);
  } catch {
    return a === b;
  }
};

/** A place's page, whatever tab or page of it is open (FE #331). */
const PLACE_PAGE = /^(\/app\/places\/[^/]+)(?:\/.*)?$/;

/**
 * On a place's page (`to`) or one under it: its Forecast tab, its sun and
 * moon pages. Its id compared whole: `ChIJparisX` is another place.
 */
const onPlacePage = (pathname: string, to: string) =>
  samePath(PLACE_PAGE.exec(pathname)?.[1] ?? pathname, to);

const stationItem = (station: WeatherStationFromList): NavItem => {
  const to = `/app/stations/${station.id}`;
  return {
    key: `favorite-${station.id}`,
    label: Station.getStationName(station),
    icon: null,
    to,
    isActive: (p: string) => p === to || p.startsWith(`${to}/`),
  };
};

/** The first five, then "See all" to the page listing them all. */
const firstFive = (items: NavItem[], allTo: string, seeAll: string) =>
  items.length > SIDEBAR_FAVORITES_MAX
    ? [
        ...items.slice(0, SIDEBAR_FAVORITES_MAX),
        {
          key: 'allFavorites',
          label: seeAll,
          icon: null,
          to: allTo,
          isActive: () => false,
        },
      ]
    : items;

/**
 * My weather's own stations and favourites (FE #381 W2, everyone's with no
 * flag), stations and places in the My weather order, under the My weather
 * entry. Nothing when there are none.
 */
const useFavoriteItems = () => {
  const {formatMessage} = useIntl();
  const {sections} = useSelector(selectMyWeatherSections);
  return React.useMemo(() => {
    const seeAll = formatMessage(messages.navigation.allFavorites);
    const items = sections.flatMap((section): NavItem[] =>
      section.id === 'myStations' || section.id === 'favorites'
        ? section.items.map((item) =>
            item.kind === 'station'
              ? stationItem(item.station)
              : {
                  key: `place-${item.place.hash}`,
                  label: item.place.title,
                  icon: null,
                  to: placeHref(item.place),
                  isActive: (p: string) =>
                    onPlacePage(p, placeHref(item.place)),
                },
          )
        : [],
    );
    return firstFive(items, '/app/', seeAll);
  }, [formatMessage, sections]);
};

/**
 * The full navigation (232px): brand, destinations, help, admin, then add
 * station / settings / contact, and the account row with the theme toggle
 * and logout. Shared by the sidebar (≥1200) and the rail's
 * expanded panel (900–1199).
 */
export const SidebarContent = ({
  onNavigate,
  headerAction,
}: {
  onNavigate?: () => void;
  /** Beside the brand: the expanded rail's collapse control. */
  headerAction?: React.ReactNode;
}) => {
  const {formatMessage} = useIntl();
  const items = useNavItems();
  const {email, firstName, lastName} = useSelector<unknown, UserInfo>(
    selectUserInfo,
    isEqual,
  );
  const name = `${firstName ?? ''} ${lastName ?? ''}`.trim();

  const logout = useLogout();
  const {pathname} = useLocation();
  const favorites = useFavoriteItems();
  // One current page: on a favourite's page the favourite is it, not My
  // weather (the phone bar, which has no favourites, keeps My weather).
  const onFavorite = favorites.some((item) => item.isActive(pathname));
  const primary = onFavorite
    ? items.primary.map((item) =>
        item.key === 'stations' ? {...item, isActive: () => false} : item,
      )
    : items.primary;

  return (
    <Box
      sx={{display: 'flex', flexDirection: 'column', height: '100%', p: 1.5}}>
      <Box sx={{display: 'flex', alignItems: 'center', mb: 1}}>
        <Box
          component={RouterLink}
          to="/"
          onClick={onNavigate}
          sx={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 1,
            py: 1,
            color: 'text.primary',
            textDecoration: 'none',
            fontWeight: 600,
            fontSize: '1rem',
            borderRadius: 1,
          }}>
          <Logo size={24} alt="" />
          SmartMixin
        </Box>
        {headerAction}
      </Box>
      <Box
        component="nav"
        aria-label={formatMessage(messages.navigation.main)}
        sx={{display: 'flex', flexDirection: 'column', flexGrow: 1, gap: 1}}>
        <NavGroup>
          {primary.map((item) => (
            <React.Fragment key={item.key}>
              <NavLink item={item} onNavigate={onNavigate} />
              {/* My weather's stations and places, nested under it. */}
              {item.key === 'stations' && favorites.length > 0 ? (
                <Box
                  component="li"
                  data-testid="shell-favorites"
                  sx={{listStyle: 'none'}}>
                  <NavGroup aria-label={item.label}>
                    {favorites.map((favorite) => (
                      <NavLink
                        key={favorite.key}
                        item={favorite}
                        nested
                        onNavigate={onNavigate}
                      />
                    ))}
                  </NavGroup>
                </Box>
              ) : null}
            </React.Fragment>
          ))}
          <NavLink item={items.help} onNavigate={onNavigate} />
          {items.admin.map((item) => (
            <NavLink key={item.key} item={item} onNavigate={onNavigate} />
          ))}
        </NavGroup>
        <Box sx={{flexGrow: 1}} />
        <NavGroup>
          {items.footer.map((item) => (
            <NavLink key={item.key} item={item} onNavigate={onNavigate} />
          ))}
        </NavGroup>
      </Box>
      <Divider sx={{my: 1}} />
      <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
        <ButtonBase
          component={RouterLink}
          to="/app/settings/account"
          onClick={onNavigate}
          disableRipple
          sx={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            p: 0.75,
            borderRadius: 1,
            justifyContent: 'flex-start',
            textAlign: 'left',
            '&:hover': {backgroundColor: 'action.hover'},
          }}>
          <Box
            component="span"
            sx={{
              display: 'flex',
              color: 'primary.main',
              '& svg': {fontSize: 28},
            }}>
            <UserCircle weight="fill" />
          </Box>
          <Box component="span" sx={{minWidth: 0}}>
            <Typography
              component="span"
              noWrap
              sx={{display: 'block', fontSize: '0.8125rem', fontWeight: 600}}>
              {name || email}
            </Typography>
            {name && (
              <Typography
                component="span"
                noWrap
                sx={{
                  display: 'block',
                  fontSize: '0.75rem',
                  color: 'text.secondary',
                }}>
                {email}
              </Typography>
            )}
          </Box>
        </ButtonBase>
        <ThemeToggleButton />
        <IconButton
          aria-label={formatMessage(messages.auth.logout)}
          onClick={logout}>
          <SignOut />
        </IconButton>
      </Box>
    </Box>
  );
};
